import {
  StyleParamType,
  wixColorParam,
  wixFontParam,
  createStylesParams,
  getSettingsValues,
} from '@wix/tpa-settings'
import {TextFieldTheme} from 'wix-ui-tpa'
import {StyleType} from '@wix/editor-platform-ooi-metadata-resolver'

import {colorMetadata, fontMetadata, numberMetadata} from '../../../utils/builderMetadata'

import {formSettingsParams} from './settingsParams'

export type FormBoxStylesParams = {
  formBoxFieldHeight: StyleParamType.Number
  formBoxFillColor: StyleParamType.Color
  formBoxBorderColor: StyleParamType.Color
  formBoxBorderWidth: StyleParamType.Number
  formBoxBorderRadius: StyleParamType.Number
  formBoxDropdownBackgroundColor: StyleParamType.Color
  formBoxSpaceBetweenFields: StyleParamType.Number
  formBoxLabelFont: StyleParamType.Font
  formBoxLabelColor: StyleParamType.Color
  formBoxTextFont: StyleParamType.Font
  formBoxTextColor: StyleParamType.Color
  formBoxSpaceBetweenFieldAndLabel: StyleParamType.Number
}

export const formBoxStylesParams = createStylesParams<FormBoxStylesParams>({
  formBoxFieldHeight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 40,
    metadata: numberMetadata(StyleType.height, {defaultValue: 40}),
  },
  formBoxFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: colorMetadata(StyleType.backgroundColor, 'color-1', 1),
  },
  formBoxBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const textFieldThemeSetting = getSettingsValues({}, formSettingsParams).textFieldTheme
      const isTextFieldBox = textFieldThemeSetting === TextFieldTheme.Box

      return wixColorParam('color-5', isTextFieldBox ? 0.6 : 1)(params)
    },
    metadata: colorMetadata(StyleType.borderColor, 'color-5', 0.6),
  },
  formBoxBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: numberMetadata(StyleType.borderWidth, {defaultValue: 1}),
  },
  formBoxBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: numberMetadata(StyleType.borderRadius, {defaultValue: 0}),
  },
  formBoxDropdownBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: colorMetadata(StyleType.backgroundColor, 'color-1', 1),
  },
  formBoxSpaceBetweenFields: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: numberMetadata(StyleType.gap, {defaultValue: 16}),
  },
  formBoxLabelFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) =>
      wixFontParam('Body-M', {
        size: params.isMobile ? 14 : 16,
      })(params),
    metadata: fontMetadata('Body-M', 16, 14),
  },
  formBoxLabelColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: colorMetadata(StyleType.color, 'color-5'),
  },
  formBoxTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) =>
      wixFontParam('Body-M', {
        size: 16,
      })(params),
    metadata: fontMetadata('Body-M', 16),
  },
  formBoxTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: colorMetadata(StyleType.color, 'color-5'),
  },
  formBoxSpaceBetweenFieldAndLabel: {
    type: StyleParamType.Number,
    getDefaultValue: () => 8,
    metadata: numberMetadata(StyleType.gap, {defaultValue: 8}),
  },
})

export type FormLineStylesParams = {
  formLineBackgroundColor: StyleParamType.Color
  formLineLineColor: StyleParamType.Color
  formLineBorderWidth: StyleParamType.Number
  formLineDropdownBackgroundColor: StyleParamType.Color
  formLineSpaceBetweenFields: StyleParamType.Number
  formLineLabelFont: StyleParamType.Font
  formLineLabelColor: StyleParamType.Color
  formLineTextFont: StyleParamType.Font
  formLineTextColor: StyleParamType.Color
  formLineSpaceBetweenFieldAndLabel: StyleParamType.Number
}

export const formLineStylesParams = createStylesParams<FormLineStylesParams>({
  formLineBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: colorMetadata(StyleType.backgroundColor, 'color-1', 0),
  },
  formLineLineColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: colorMetadata(StyleType.borderColor, 'color-5', 1),
  },
  formLineBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: numberMetadata(StyleType.borderWidth, {defaultValue: 1}),
  },
  formLineDropdownBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: colorMetadata(StyleType.backgroundColor, 'color-1', 1),
  },
  formLineSpaceBetweenFields: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: numberMetadata(StyleType.gap, {defaultValue: 16}),
  },
  formLineLabelFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {size: 14}),
    metadata: fontMetadata('Body-M', 14),
  },
  formLineLabelColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: colorMetadata(StyleType.color, 'color-5'),
  },
  formLineTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) =>
      wixFontParam('Body-M', {
        size: 16,
      })(params),
    metadata: fontMetadata('Body-M', 16),
  },
  formLineTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: colorMetadata(StyleType.color, 'color-5'),
  },
  formLineSpaceBetweenFieldAndLabel: {
    type: StyleParamType.Number,
    getDefaultValue: () => 8,
    metadata: numberMetadata(StyleType.gap, {defaultValue: 8}),
  },
})
